import React, { useState, useEffect } from 'react';
import { Modal, Form, DatePicker, Input, Card, message } from 'antd';
import Button from './button';
import dayjs from 'dayjs';
import { updateAdvertisement } from '../../api/ads';
import Close from '../../assets/icons/close.svg';
import colors from '../../share/constants/constants';
import Dropdown from './dropdown';
import { UsaStates } from 'usa-states';

const EditAdvertisementModal = ({
  onCancel,
  advertisement,
  advertisementTypes,
  offerTypes,
  onSuccess
}) => {
  const [form] = Form.useForm();
  const [inProgress, setInProgress] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const usStates = new UsaStates();
  
  const handleFieldChange = (fieldName, value) => {
    form.setFieldsValue({ [fieldName]: value });
    
    if (fieldName === 'startDate') {
      setStartDate(value);
    } else if (fieldName === 'endDate') {
      setEndDate(value);
    }
  };
  
  const days = [
    { name: "1 Day", _id: "1" },
    { name: "2 Days", _id: "2" },
    { name: "3 Days", _id: "3" },
    { name: "4 Days", _id: "4" },
    { name: "5 Days", _id: "5" },
    { name: "6 Days", _id: "6" },
    { name: "7 Days", _id: "7" },
    { name: "8 Days", _id: "8" },
    { name: "9 Days", _id: "9" },
    { name: "10 Days", _id: "10" },
    { name: "11 Days", _id: "11" },
    { name: "12 Days", _id: "12" },
    { name: "13 Days", _id: "13" },
    { name: "14 Days", _id: "14" },
    { name: "15 Days", _id: "15" },
    { name: "16 Days", _id: "16" },
    { name: "17 Days", _id: "17" },
    { name: "18 Days", _id: "18" },
    { name: "19 Days", _id: "19" },
    { name: "20 Days", _id: "20" },
    { name: "21 Days", _id: "21" },
    { name: "22 Days", _id: "22" },
    { name: "23 Days", _id: "23" },
    { name: "24 Days", _id: "24" },
    { name: "25 Days", _id: "25" },
    { name: "26 Days", _id: "26" },
    { name: "27 Days", _id: "27" },
    { name: "28 Days", _id: "28" },
    { name: "29 Days", _id: "29" },
    { name: "30 Days", _id: "30" },
  ];
  
  const stateOptions = usStates.states.map(state => ({
    name: state.name,
    _id: state.abbreviation
  }));

  useEffect(() => {
    if (advertisement) {
      const startDateValue = advertisement.startDate ? dayjs(advertisement.startDate) : null;
      const endDateValue = advertisement.endDate ? dayjs(advertisement.endDate) : null;

      setStartDate(startDateValue);
      setEndDate(endDateValue);
      
      form.setFieldsValue({
        numberOfDays: advertisement.numberOfDays?.toString(),
        startDate: startDateValue,
        endDate: endDateValue,
        city: advertisement.city,
        state: advertisement.state,
        advertisementTypeId: advertisement.advertisementTypeId?._id,
        offerTypeId: advertisement.offerTypeId?._id,
      });
    }
  }, [advertisement, form]);

  const disabledEndDate = (current) => {
    const start = form.getFieldValue('startDate');
    return start && current && current.isBefore(start, 'day');
  };

  const onFinish = async (values) => {
    try {
      setInProgress(true);
      
      if (values.startDate) {
        values.startDate = values.startDate.toISOString();
      }
      if (values.endDate) {
        values.endDate = values.endDate.toISOString();
      }
      
      if (values.numberOfDays) {
        values.numberOfDays = parseInt(values.numberOfDays);
      }

      const response = await updateAdvertisement(advertisement._id, values);
      if (response) {
        message.success('Advertisement updated successfully');
        setInProgress(false);
        onSuccess();
      } else {
        setInProgress(false);
      }
    } catch (error) {
      console.error('Validation failed:', error);
      setInProgress(false);
    }
  };

  return (
    <div>
      <Modal
        open={true}
        footer={null}
        closeIcon={null}
        width="436px"
        onCancel={onCancel}
      >
        <Card>
          <div className="flex">
            <h2 className="flex-1">Edit Advertisement</h2>
            <img
              src={Close}
              alt="Cross Icon"
              className="w-3.2 hover:cursor-pointer"
              onClick={onCancel}
            />
          </div>
          <Form form={form} onFinish={onFinish}>
            <div className="flex flex-col justify-center">
              <Form.Item
                name="numberOfDays"
                rules={[{ required: false }]}
              >
                <div>
                  <p className="text-1.4">Number of Days</p>
                  <Dropdown
                    className={'w-full h-5.4 !rounded-xl'}
                    placeholder="Select number of days"
                    data={days}
                    defaultValue={advertisement?.numberOfDays?.toString()}
                    onChange={(value) => handleFieldChange('numberOfDays', value)}
                  />
                </div>
              </Form.Item>
              
              <div className="flex gap-4">
                <Form.Item
                  name="startDate"
                  rules={[{ required: false }]}
                  className="w-1/2"
                >
                  <div>
                    <p className="text-1.4">Start Date</p>
                    <DatePicker 
                      className="w-full h-5.4 !rounded-xl focus:bg-white focus:border-primaryPurple hover:border-primaryPurple" 
                      placeholder="Start Date"
                      format="YYYY-MM-DD"
                      showTime={false}
                      disabledDate={(current)=> current && current.isAfter(dayjs().add(5,'year'))} 
                      onChange={(date) => handleFieldChange('startDate', date)}
                      value={startDate}
                    />
                  </div>
                </Form.Item>
                
                <Form.Item
                  name="endDate"
                  rules={[{
                    required: false,
                    validator: (_, value) => {
                      const start = form.getFieldValue('startDate');
                      if (!value || !start || value.isAfter(start, 'day')) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('End date must be after start date'));
                    }
                  }]} 
                  className="w-1/2"
                >
                  <div>
                    <p className="text-1.4">End Date</p>
                    <DatePicker 
                      className="w-full h-5.4 !rounded-xl focus:bg-white focus:border-primaryPurple hover:border-primaryPurple" 
                      placeholder="End Date"
                      format="YYYY-MM-DD"
                      showTime={false}
                      disabledDate={disabledEndDate}
                      onChange={(date) => handleFieldChange('endDate', date)}
                      value={endDate}
                    />
                  </div>
                </Form.Item>
              </div>
              
              <Form.Item
                name="city"
                rules={[{ required: false }]}
              >
                <div>
                  <p className="text-1.4">City</p>
                  <Input
                    placeholder="Enter city"
                    defaultValue={advertisement?.city || ''}
                    className={'w-full h-5.4 !rounded-xl border-unset'}
                  />
                </div>
              </Form.Item>
              
              <Form.Item
                name="state"
                rules={[{ required: false }]}
              >
                <div>
                  <p className="text-1.4">State</p>
                  <Dropdown
                    className={'w-full h-5.4 !rounded-xl'}
                    placeholder="Select state"
                    data={stateOptions}
                    defaultValue={advertisement?.state}
                    onChange={(value) => handleFieldChange('state', value)}
                    loadMoreData={() => {}}
                  />
                </div>
              </Form.Item>
              
              <Form.Item
                name="advertisementTypeId"
                rules={[{ required: false }]}
              >
                <div>
                  <p className="text-1.4">Advertisement Type</p>
                  <Dropdown
                    className={'w-full h-5.4 !rounded-xl'}
                    placeholder="Select advertisement type"
                    data={advertisementTypes?.map(type => ({ name: type.name, _id: type._id })) || []}
                    defaultValue={advertisement?.advertisementTypeId?._id}
                    onChange={(value) => handleFieldChange('advertisementTypeId', value)}
                  />
                </div>
              </Form.Item>
              
              <Form.Item
                name="offerTypeId"
                rules={[{ required: false }]}
              >
                <div>
                  <p className="text-1.4">Offer Type</p>
                  <Dropdown
                    className={'w-full h-5.4 !rounded-xl'}
                    placeholder="Select offer type"
                    data={offerTypes?.map(type => ({ name: type.name, _id: type._id })) || []}
                    defaultValue={advertisement?.offerTypeId?._id}
                    onChange={(value) => handleFieldChange('offerTypeId', value)}
                  />
                </div>
              </Form.Item>
            </div>
            
            <Form.Item>
              <Button
                title="Update"
                color={colors.primaryPurple}
                className="flex items-center w-full"
                htmlType="submit"
                loading={inProgress}
              />
            </Form.Item>
          </Form>
        </Card>
      </Modal>
    </div>
  );
};

export default EditAdvertisementModal;
